import {Space, Spin, Tooltip} from 'antd';
import React, {useEffect} from 'react';
import {useDispatch, useIntl, useSelector} from 'umi';
import pick from 'lodash/pick';

import {ServiceRequestModel} from '@/typings/models/ServiceRequest';
import AgentName from '@/components/Agent/AgentName';
import {checkNullString} from '@/utils/prettyname';
import styles from './index.less';
import LeadAssigneeDropdownFilters from '@/components/Filters/LeadAssigneeDropdownFilters';
import {useHasAccess, useServiceRequestPermissions} from '@/utils/hooks';
import PERMISSIONS from '@/constants/permissions';
import classNames from 'classnames';
import {getServiceRequestAssignee, isServiceRequestStatusEditable} from '@/components/Kanban/ServiceRequests/Calendar/utils';
import {editCalendarEventRequest, editServiceRequestRequest} from '@/services/api/serviceRequest';
import * as LocationSelectors from '@/selectors/locationSelectors';
import {confirmIfAgentServiceMismatch} from '@/utils/agentServiceMismatch';
import {formatToISOStringWithOffset} from '@/utils/date';
import {isRecurringEvent} from '@/utils/recurrence';
import {useRecurrenceScope} from '@/components/Kanban/ServiceRequests/Calendar/Recurrence/useRecurrenceScope';

const ServiceRequestAssignee = ({
  serviceRequest,
  selectProps,
}: {
  serviceRequest: ServiceRequestModel;
  selectProps?: Record<string, any>;
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { askRecurrenceScope, recurrenceScopeDrawer } = useRecurrenceScope();
  const fullBranches = useSelector(LocationSelectors.selectFullBranches);

  const assignee = getServiceRequestAssignee(serviceRequest);
  const assigneeName = assignee
    ? `${checkNullString(assignee.firstname)} ${checkNullString(assignee.lastname)}`
    : '';

  const hasCalendarEventsCreateForOthers = useHasAccess([
    PERMISSIONS.CalendarEvents.CREATE_FOR_OTHERS,
    PERMISSIONS.BUSINESS_PARTNER,
  ]);
  const { canEditServiceRequest } = useServiceRequestPermissions(serviceRequest);

  const canAssignCalendarEvent = serviceRequest?.blocker ? hasCalendarEventsCreateForOthers : true;
  // A cancelled/declined/rescheduled appointment is locked regardless of the
  // no-assignee escape hatch below, so gate the whole expression on status.
  const canAssign =
    isServiceRequestStatusEditable(serviceRequest) &&
    canAssignCalendarEvent &&
    (canEditServiceRequest || !assignee?.id);

  // Pre-fetch the branch with full service/agent detail if it's not yet in the store.
  // This way the agent list is ready by the time the user opens the assignee dropdown.
  useEffect(() => {
    const branchId = serviceRequest?.branch_service?.branch?.id;
    if (!branchId) return;
    const alreadyLoaded = fullBranches.some(
      (b: any) => `${b.id}` === `${branchId}` && (b.services || []).length > 0,
    );
    if (alreadyLoaded) return;
    dispatch({ type: 'location/fetchFullBranchesWithDetails', payload: { branches: `${branchId}` } });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAssigneeChange = async (rawAssigneeId, rawAssigneeObject) => {
    // Clearing the select yields undefined/null — normalize to null so the API
    // receives an explicit `assignee: null` (an undefined value is dropped by
    // JSON.stringify, which the backend reads as "leave the assignee as is").
    const newAssigneeId = rawAssigneeId ?? null;
    const newAssigneeObject = rawAssigneeObject ?? null;
    const currentServiceId = serviceRequest?.branch_service?.id;
    const currentServiceName = serviceRequest?.branch_service?.service_type?.name || '';

    // 1. Agents embedded on the service request object itself
    let serviceAgents: any[] = serviceRequest?.branch_service?.agents || [];
    // 2. Agents from the Redux store (populated by earlier fetches)
    if (serviceAgents.length === 0 && currentServiceId) {
      for (const branch of fullBranches) {
        const found = (branch.services || []).find((s: any) => `${s.id}` === `${currentServiceId}`);
        if (found) {
          serviceAgents = found.agents || [];
          break;
        }
      }
    }

    const newAgentName = [newAssigneeObject?.firstname, newAssigneeObject?.lastname]
      .filter(Boolean)
      .join(' ');

    const performChange = async () => {
      let applyTo;
      // Recurring custom event: ask which occurrences the assignee change applies to.
      if (serviceRequest.blocker && isRecurringEvent(serviceRequest as any)) {
        const scope = await askRecurrenceScope({ action: 'edit' });
        if (!scope) return;
        applyTo = scope;
      }
      dispatch({
        type: 'serviceRequest/updateCalendarEvent',
        payload: {
          event: {
            id: serviceRequest?.id,
            // Calendar events carry their assignee on `assignee`; `agent` is the
            // appointment field (see getServiceRequestAssignee) — writing the
            // wrong one leaves the old assignee visible.
            ...(serviceRequest?.blocker
              ? { assignee: newAssigneeObject, assigneeId: newAssigneeId }
              : { agent: newAssigneeObject }),
          },
        },
      });
      if (serviceRequest.blocker) {
        // TODO Change this implementation when PATCH is provided by backend.
        editCalendarEventRequest({
          params: { calendarEventId: serviceRequest.id },
          data: {
            ...pick(serviceRequest, ['title', 'description', 'color']),
            // Same payload as a calendar drag/drop: local time with offset —
            // utc_start_time here is the processed Date, not the API string.
            start: formatToISOStringWithOffset(new Date(serviceRequest.utc_start_time)),
            end: formatToISOStringWithOffset(new Date(serviceRequest.utc_end_time)),
            assignee: newAssigneeId,
            ...(applyTo ? { apply_to: applyTo } : {}),
          },
        });
      } else {
        editServiceRequestRequest({
          params: { serviceRequestId: serviceRequest?.id },
          data: { assignee: newAssigneeId },
        });
      }
    };

    // No service attached (always the case for custom calendar events): the
    // mismatch check is meaningless and its modal would block the change.
    if (!currentServiceId) {
      performChange();
      return;
    }

    confirmIfAgentServiceMismatch({
      agentId: newAssigneeId,
      agentName: newAgentName,
      serviceAgents,
      serviceName: currentServiceName,
      intl,
      onConfirm: performChange,
    });
  };

  const isCalendarEventReadonly = serviceRequest?.blocker && !hasCalendarEventsCreateForOthers;
  if (isCalendarEventReadonly) {
    if (!assignee) return <> - </>;
    return (
      <Tooltip
        placement="topLeft"
        trigger={['hover']}
        title={
          <div className={styles.assigneeTooltip}>
            <span>{assigneeName}</span>
            {assignee?.email && (
              <div>
                <small>{assignee.email}</small>
              </div>
            )}
          </div>
        }
      >
        <Space>
          <AgentName agent={assignee} />
        </Space>
      </Tooltip>
    );
  }

  return (
    <>
      <Spin spinning={false}>
        <LeadAssigneeDropdownFilters
          filterMode="select"
          onlyAssignable
          type="dropdown"
          className={classNames(styles.leadAssigneeOverview, {
            [styles.disabledAssignee]: !canAssign,
          })}
          value={assignee?.id}
          assigneeObject={assignee}
          hideDisabled
          selectProps={{
            variant: 'borderless',
            ...selectProps,
            disabled: !canAssign,
          }}
          onChange={handleAssigneeChange}
        />
      </Spin>
      {recurrenceScopeDrawer}
    </>
  );
};

export default ServiceRequestAssignee;
